import classNames from 'classnames/bind';
import { useState } from 'react';
import useTimeout from '@flight-workspace/common-client/src/hooks/useTimeout';
import { ScheduleType, SearchType } from 'contexts/searchParams';
import { IScheduleDetail } from 'list/international/types';
import DetailScheduleLayer from './detailScheduleLayer';
import styles from './index.module.scss';
import { IKeyStringValue } from 'common/types';

const cx = classNames.bind(styles);

interface IProps {
  departureTime: string;
  arrivalTime: string;
  departureCity: string;
  arrivalCity: string;
  dayDiff: number;
  stop?: number;
  searchType?: SearchType;
  journeyTime: string | string[];
  schedule?: IScheduleDetail[];
  departureHighlight?: boolean;
  arrivalHighlight?: boolean;
  isList?: boolean;
  scheduleType?: ScheduleType;
  airlines?: IKeyStringValue;
  airports?: IKeyStringValue;
  showHelpTooltip?: boolean;
}

enum TooltipDisplay {
  FADEOUT = 'fadeout',
  HIDE = 'hide',
  SHOW = 'show',
}

export default function Route({
  departureTime,
  arrivalTime,
  departureCity,
  arrivalCity,
  dayDiff,
  stop,
  searchType,
  journeyTime,
  schedule,
  departureHighlight = false,
  arrivalHighlight = false,
  isList,
  scheduleType,
  airlines,
  airports,
  showHelpTooltip,
}: IProps) {
  const [hour, minute] = Array.isArray(journeyTime)
    ? journeyTime.map(t => t.padStart(2, '0'))
    : journeyTime.split(':').map(t => t.padStart(2, '0'));
  const [showTooltip, setShowTooltip] = useState(false);

  const onOpen = () => setShowTooltip(true);
  const onClose = () => setShowTooltip(false);

  const [tooltipDisplay, setTooltipDisplay] = useState<TooltipDisplay>(
    TooltipDisplay.SHOW,
  );

  useTimeout(() => {
    if (tooltipDisplay !== TooltipDisplay.HIDE) {
      setTooltipDisplay(TooltipDisplay.FADEOUT);
    }
  }, 3000);

  useTimeout(() => {
    if (tooltipDisplay !== TooltipDisplay.HIDE) {
      setTooltipDisplay(TooltipDisplay.HIDE);
    }
  }, 4000);

  return (
    <div className={cx('Route')}>
      <span className={cx('airport')}>
        <b className={cx('time')}>{departureTime}</b>
        <i className={cx('code')} data-different={departureHighlight}>
          {departureCity}
        </i>
      </span>
      <span className={cx('airport')}>
        <b className={cx('time')}>{arrivalTime}</b>
        <i className={cx('code')} data-different={arrivalHighlight}>
          {arrivalCity}
        </i>
      </span>
      {dayDiff > 0 && <b className={cx('extra')}>{`+${dayDiff}일`}</b>}
      <div className={cx('info')}>
        {searchType === SearchType.INTERNATIONAL &&
          (isList ? (
            <>
              <button
                className={cx('details')}
                type="button"
                onClick={onOpen}
                aria-haspopup="true"
                aria-expanded={showTooltip}
              >
                {!!stop ? `경유 ${stop}` : '직항'}, {hour}시간 {minute}분
              </button>
              {showHelpTooltip && (
                <div
                  className={cx('ly_tooltip')}
                  role="tooltip"
                  data-animating={tooltipDisplay}
                >
                  <div className={cx('inner')}>
                    <b>상세 여정</b>을 확인해보세요
                  </div>
                </div>
              )}
              {showTooltip && !!schedule && !!airlines && !!airports && (
                <DetailScheduleLayer
                  onClose={onClose}
                  schedule={schedule}
                  scheduleType={scheduleType}
                  airlines={airlines}
                  airports={airports}
                />
              )}
            </>
          ) : (
            <>
              {!!stop ? `경유 ${stop}` : '직항'}, {hour}시간 {minute}분
            </>
          ))}
        {searchType !== SearchType.INTERNATIONAL && `${hour}시간 ${minute}분`}
      </div>
    </div>
  );
}
